import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useForm, Controller } from 'react-hook-form';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowLeftIcon, ArrowPathIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';

/**
 * Alta / edición de proveedor como página (Comercial → Proveedores → Nuevo/Editar).
 * Reemplaza los modales; el formulario es el mismo, con búsqueda en ARCA por documento.
 */

const EMPTY_DEFAULTS = {
  type: 'company',
  companyName: '',
  firstName: '',
  lastName: '',
  fantasyName: '',
  email: '',
  phone: '',
  contactPerson: '',
  contactPhone: '',
  address: '',
  city: '',
  state: '',
  zipCode: '',
  importanceLevel: 'regular',
  paymentMethod: '',
  avgTermDays: '',
  dueMode: 'days',
  discountBenefit: '',
  alsoClient: false,
};

export default function ProviderForm() {
  const navigate = useNavigate();
  const { id: editId } = useParams<{ id: string }>();
  const isEditing = Boolean(editId);

  const [documentNumber, setDocumentNumber] = useState('');
  const [searchingARCA, setSearchingARCA] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadingProvider, setLoadingProvider] = useState(isEditing);

  const { register, handleSubmit, control, watch, setValue, reset, formState: { errors } } = useForm({ defaultValues: EMPTY_DEFAULTS });
  const watchType = watch('type', 'company');
  const watchDueMode = watch('dueMode', 'days');

  // Modo edición: cargar el proveedor y precargar el formulario
  useEffect(() => {
    if (!editId) return;
    axios.get(`/api/providers/${editId}`)
      .then((r) => {
        const p = r.data?.provider || r.data;
        if (!p) throw new Error('not-found');
        const words = String(p.name || '').trim().split(/\s+/).filter(Boolean);
        const lastName = words.length > 1 ? words[words.length - 1] : '';
        const firstName = words.length > 1 ? words.slice(0, -1).join(' ') : (words[0] || '');
        reset({
          ...EMPTY_DEFAULTS,
          ...p,
          type: p.type || 'company',
          firstName,
          lastName,
          avgTermDays: p.avgTermDays != null ? String(p.avgTermDays) : '',
          dueMode: p.dueMode || 'days',
          alsoClient: Boolean(p.clientId),
        });
        setDocumentNumber(p.taxId || p.document || '');
      })
      .catch(() => {
        toast.error('No encontramos el proveedor');
        navigate('/providers');
      })
      .finally(() => setLoadingProvider(false));
  }, [editId, navigate, reset]);

  const searchARCA = async () => {
    if (!documentNumber || documentNumber.trim() === '') {
      toast.error('Por favor, ingresá un número de documento (DNI, CUIT o CUIL)');
      return;
    }
    setSearchingARCA(true);
    try {
      const response = await axios.post('/api/clients/search-afip', { document: documentNumber.trim() }, { timeout: 20000 });
      const data = response.data?.data || {};
      const cleanDoc = (data.cuit || documentNumber.trim()).replace(/\D/g, '');
      if (data.personType === 'JURIDICA' || data.companyName) {
        setValue('type', 'company');
        await new Promise(r => setTimeout(r, 50));
        setValue('companyName', data.companyName || '');
      } else {
        setValue('type', 'individual');
        await new Promise(r => setTimeout(r, 50));
        setValue('firstName', data.firstName || '');
        setValue('lastName', data.lastName || '');
      }
      if (cleanDoc) setDocumentNumber(cleanDoc);
      if (data.address) {
        const street = [data.address.street, data.address.streetNumber].filter(Boolean).join(' ');
        setValue('address', street || '');
        setValue('city', data.address.city || '');
        setValue('state', data.address.province || '');
        setValue('zipCode', data.postalCode || '');
      }
      toast.success('Datos cargados desde ARCA');
    } catch (error: any) {
      if (error.code === 'ECONNABORTED') {
        toast.error('La consulta a ARCA tardó demasiado. Intentá de nuevo.');
      } else {
        toast.error(error.response?.data?.error || 'Error al buscar en ARCA');
      }
    } finally {
      setSearchingARCA(false);
    }
  };

  const cleanName = (raw: string, fallback: string) => {
    let n = String(raw || '').trim().replace(/^\s*0+\s*/, '').replace(/\s*0+\s*$/, '').trim();
    return n === '' ? fallback : n;
  };

  const onSubmit = async (data: any) => {
    if (!data.type) data.type = 'company';

    if (data.type === 'company') {
      if (!data.companyName || data.companyName === '0' || String(data.companyName).trim() === '') {
        toast.error('El nombre de la empresa es inválido');
        return;
      }
      data.name = cleanName(data.companyName, 'Empresa sin nombre');
      data.companyName = data.name;
    } else if (data.type === 'individual') {
      const firstName = data.firstName?.trim() || '';
      const lastName = data.lastName?.trim() || '';
      data.name = cleanName(`${firstName} ${lastName}`.trim() || 'Sin nombre', 'Sin nombre');
    }

    delete data.paymentTerms;
    delete data.firstName;
    delete data.lastName;

    for (const f of ['email', 'phone', 'contactPerson', 'contactPhone']) {
      if (!data[f] || String(data[f]).trim() === '') delete data[f];
    }
    if (!data.importanceLevel) data.importanceLevel = 'regular';
    if (data.paymentMethod === '') delete data.paymentMethod;
    if (data.discountBenefit !== undefined && data.discountBenefit.trim?.() === '') delete data.discountBenefit;
    if (data.avgTermDays !== undefined) {
      if (`${data.avgTermDays}`.trim() === '') {
        delete data.avgTermDays;
      } else {
        const parsed = Number(data.avgTermDays);
        if (Number.isNaN(parsed)) delete data.avgTermDays; else data.avgTermDays = parsed;
      }
    }
    if (documentNumber && documentNumber.trim()) {
      data.taxId = documentNumber.trim().replace(/\D/g, '') || documentNumber.trim();
    }

    setSaving(true);
    try {
      if (isEditing) {
        await axios.put(`/api/providers/${editId}`, data);
        toast.success('Proveedor actualizado exitosamente');
      } else {
        await axios.post('/api/providers', data);
        toast.success('Proveedor creado exitosamente');
      }
      navigate('/providers');
    } catch (error: any) {
      toast.error(error.response?.data?.error || `Error al ${isEditing ? 'actualizar' : 'crear'} el proveedor`);
    } finally {
      setSaving(false);
    }
  };

  const fieldCls = 'px-4 py-3 rounded-xl border border-input bg-background';

  if (loadingProvider) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary" /></div>;
  }

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <div className="mx-auto w-full max-w-3xl space-y-6">
        <div className="flex items-center gap-3">
          <Button type="button" variant="outline" size="icon" className="shrink-0" onClick={() => navigate('/providers')}>
            <ArrowLeftIcon className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">{isEditing ? 'Editar proveedor' : 'Nuevo proveedor'}</h1>
            <p className="text-sm text-muted-foreground">
              {isEditing ? 'Actualizá los datos del proveedor.' : 'Cargá los datos del proveedor. Podés autocompletar con el documento desde ARCA.'}
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
          {/* Número de Documento + ARCA */}
          <div className="space-y-2">
            <Label htmlFor="document-number" className="font-medium text-sm">Número de Documento</Label>
            <div className="flex gap-2">
              <Input
                id="document-number"
                value={documentNumber}
                onChange={(e) => setDocumentNumber(e.target.value)}
                className={`flex-1 ${fieldCls}`}
                placeholder="DNI, CUIT o CUIL"
              />
              <Button
                type="button"
                onClick={searchARCA}
                disabled={searchingARCA || !documentNumber.trim()}
                className="shrink-0 h-auto gap-2 text-white hover:opacity-90"
                style={{ backgroundColor: '#2D3355', borderColor: '#2D3355' }}
              >
                {searchingARCA ? <><ArrowPathIcon className="size-4 animate-spin" />Buscando...</> : <><MagnifyingGlassIcon className="size-4" />Buscar en ARCA</>}
              </Button>
            </div>
          </div>

          <div className="space-y-2">
            <Label className="font-medium text-sm">Tipo *</Label>
            <Controller
              name="type"
              control={control}
              rules={{ required: 'Tipo es requerido' }}
              render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger className={fieldCls}><SelectValue placeholder="Seleccionar tipo..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="individual">Individual</SelectItem>
                    <SelectItem value="company">Empresa</SelectItem>
                  </SelectContent>
                </Select>
              )}
            />
            {errors.type && <p className="text-sm text-destructive">{errors.type?.message as string}</p>}
          </div>

          {watchType === 'company' && (
            <div className="space-y-2">
              <Label htmlFor="companyName" className="font-medium text-sm">Nombre de la Empresa *</Label>
              <Input id="companyName" {...register('companyName', { required: watchType === 'company' ? 'Nombre de la empresa es requerido' : false })} className={fieldCls} placeholder="Nombre de la empresa" />
              {errors.companyName && <p className="text-sm text-destructive">{errors.companyName?.message as string}</p>}
            </div>
          )}

          {watchType === 'individual' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="firstName" className="font-medium text-sm">Nombre *</Label>
                <Input id="firstName" {...register('firstName', { required: watchType === 'individual' ? 'Nombre es requerido' : false })} className={fieldCls} placeholder="Nombre" />
                {errors.firstName && <p className="text-sm text-destructive">{errors.firstName?.message as string}</p>}
              </div>
              <div className="space-y-2">
                <Label htmlFor="lastName" className="font-medium text-sm">Apellido *</Label>
                <Input id="lastName" {...register('lastName', { required: watchType === 'individual' ? 'Apellido es requerido' : false })} className={fieldCls} placeholder="Apellido" />
                {errors.lastName && <p className="text-sm text-destructive">{errors.lastName?.message as string}</p>}
              </div>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="fantasyName" className="font-medium text-sm">Nombre de fantasía</Label>
            <Input id="fantasyName" {...register('fantasyName')} className={fieldCls} placeholder='Cómo conocés al negocio (ej: "Repuestos San Lorenzo")' />
            <p className="text-xs text-muted-foreground">Se muestra en compras, listas y búsquedas. Útil cuando facturan con su nombre personal.</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="font-medium text-sm">Email</Label>
              <Input id="email" type="email" {...register('email')} className={fieldCls} placeholder="email@ejemplo.com" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone" className="font-medium text-sm">Teléfono</Label>
              <Input id="phone" {...register('phone')} className={fieldCls} placeholder="+54 11 1234-5678" />
            </div>
          </div>

          {watchType === 'company' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="contactPerson" className="font-medium text-sm">Persona de Contacto</Label>
                <Input id="contactPerson" {...register('contactPerson')} className={fieldCls} placeholder="Nombre del contacto" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="contactPhone" className="font-medium text-sm">Teléfono del Contacto</Label>
                <Input id="contactPhone" {...register('contactPhone')} className={fieldCls} placeholder="Teléfono del contacto" />
              </div>
            </div>
          )}

          <div className="rounded-lg border border-border bg-muted/20 p-4 space-y-4">
            <p className="text-sm font-semibold">Condiciones comerciales</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="font-medium text-sm">Criticidad</Label>
                <Controller
                  name="importanceLevel"
                  control={control}
                  render={({ field }) => (
                    <Select value={field.value} onValueChange={field.onChange}>
                      <SelectTrigger className={fieldCls}><SelectValue placeholder="Seleccionar criticidad" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="critico">Critico</SelectItem>
                        <SelectItem value="regular">Regular</SelectItem>
                        <SelectItem value="alternativo">Alternativo</SelectItem>
                        <SelectItem value="problematico">Problematico</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>
              <div className="space-y-2">
                <Label className="font-medium text-sm">Forma de pago habitual</Label>
                <Controller
                  name="paymentMethod"
                  control={control}
                  render={({ field }) => (
                    <Select value={field.value || ''} onValueChange={field.onChange}>
                      <SelectTrigger className={fieldCls}><SelectValue placeholder="Seleccionar forma de pago" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="contado">Contado</SelectItem>
                        <SelectItem value="cta_cte">Cta Cte</SelectItem>
                        <SelectItem value="cheque">Cheque</SelectItem>
                        <SelectItem value="transferencia">Transferencia</SelectItem>
                        <SelectItem value="otro">Otro</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="font-medium text-sm">Vencimiento del pago</Label>
                <Controller
                  name="dueMode"
                  control={control}
                  render={({ field }) => (
                    <Select value={field.value || 'days'} onValueChange={field.onChange}>
                      <SelectTrigger className={fieldCls}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="days">A X días de la compra</SelectItem>
                        <SelectItem value="end_of_month">Fin de mes</SelectItem>
                        <SelectItem value="end_of_month_plus">Fin de mes + X días</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                />
                {watchDueMode !== 'end_of_month' && (
                  <Input type="number" {...register('avgTermDays')} className={fieldCls} placeholder={watchDueMode === 'end_of_month_plus' ? 'días después de fin de mes' : '30'} min={0} />
                )}
                <p className="text-xs text-muted-foreground">
                  {watchDueMode === 'end_of_month' ? 'Vence el último día del mes de la compra.'
                    : watchDueMode === 'end_of_month_plus' ? 'Vence a esa cantidad de días después de fin de mes.'
                    : 'Vence a esa cantidad de días desde cada compra.'}
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="discountBenefit" className="font-medium text-sm">Descuento / beneficio</Label>
                <Input id="discountBenefit" {...register('discountBenefit')} className={fieldCls} placeholder="Ej: 10% contado" />
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="address" className="font-medium text-sm">Dirección</Label>
            <Textarea id="address" {...register('address')} rows={3} className={fieldCls} placeholder="Dirección completa" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="city" className="font-medium text-sm">Ciudad</Label>
              <Input id="city" {...register('city')} className={fieldCls} placeholder="Ciudad" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="state" className="font-medium text-sm">Provincia</Label>
              <Input id="state" {...register('state')} className={fieldCls} placeholder="Provincia" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="zipCode" className="font-medium text-sm">Código Postal</Label>
              <Input id="zipCode" {...register('zipCode')} className={fieldCls} placeholder="CP" />
            </div>
          </div>

          <div className="border-t pt-4">
            <div className="flex items-center gap-2">
              <input type="checkbox" id="alsoClient" {...register('alsoClient')} className="rounded" />
              <Label htmlFor="alsoClient" className="font-normal cursor-pointer">También es cliente</Label>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Crea (o vincula por CUIT/nombre) la ficha en Clientes. Las deudas entre las dos cuentas corrientes se compensan automáticamente.
            </p>
          </div>

          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end pt-2">
            <Button type="button" variant="outline" onClick={() => navigate('/providers')}>Cancelar</Button>
            <Button type="submit" disabled={saving} className="bg-black dark:bg-white text-white dark:text-black hover:bg-black/90 dark:hover:bg-white/90">
              {saving ? 'Guardando...' : isEditing ? 'Guardar cambios' : 'Crear proveedor'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
